import { apiInterfaceForJson, apiInterfaceForFormData } from "../common/apiManager";

const ARRAY_FIELDS = ["statuses", "excludeStatuses", "country", "excludeCountries", "desk", "agent", "floorManager", "source", "affiliate", "callCountRange"];
const MAX_GET_ITEMS = 30;

function buildFilterBody(filters, extra = {}) {
    const body = { ...extra };
    for (const key of ARRAY_FIELDS) {
        if (filters[key] && filters[key].length > 0) body[key] = filters[key];
    }
    if (filters.searchTerm) body.searchTerm = filters.searchTerm;
    if (filters.hasAffiliate === true) body.hasAffiliate = "true";
    if (filters.isLive !== undefined && filters.isLive !== null) body.isLive = filters.isLive;
    if (filters.hasBeenTransferred !== undefined && filters.hasBeenTransferred !== null) body.hasBeenTransferred = filters.hasBeenTransferred;
    if (filters.registeredFrom) body.registeredFrom = filters.registeredFrom;
    if (filters.registeredTo) body.registeredTo = filters.registeredTo;
    if (filters.commentsFrom) body.commentsFrom = filters.commentsFrom;
    if (filters.commentsTo) body.commentsTo = filters.commentsTo;
    return body;
}

function shouldUsePost(filters) {
    let total = 0;
    for (const key of ARRAY_FIELDS) {
        if (filters[key] && Array.isArray(filters[key])) total += filters[key].length;
    }
    return total > MAX_GET_ITEMS;
}

function buildGetParams(filters) {
    const params = new URLSearchParams();
    for (const key of ARRAY_FIELDS) {
        if (filters[key] && filters[key].length > 0) {
            filters[key].forEach(v => params.append(key, v));
        }
    }
    if (filters.searchTerm) params.append("searchTerm", filters.searchTerm);
    if (filters.hasAffiliate === true) params.append("hasAffiliate", "true");
    if (filters.isLive !== undefined && filters.isLive !== null) params.append("isLive", filters.isLive);
    if (filters.hasBeenTransferred !== undefined && filters.hasBeenTransferred !== null) params.append("hasBeenTransferred", filters.hasBeenTransferred);
    if (filters.registeredFrom) params.append("registeredFrom", filters.registeredFrom);
    if (filters.registeredTo) params.append("registeredTo", filters.registeredTo);
    if (filters.commentsFrom) params.append("commentsFrom", filters.commentsFrom);
    if (filters.commentsTo) params.append("commentsTo", filters.commentsTo);
    return params;
}

export const fetchLeads = () => {
    return apiInterfaceForJson("/lead/v1/", "GET");
};

export const fetchAvailableLeads = (page = 1, limit = 100, filters = {}, sortConfig) => {
    if (shouldUsePost(filters)) {
        const body = buildFilterBody(filters, { page, limit });
        if (sortConfig && sortConfig.key) {
            body.sortKey = sortConfig.key;
            if (sortConfig.direction) body.sortDirection = sortConfig.direction;
        }
        return apiInterfaceForJson("/lead/v1/available", "POST", body);
    }

    const params = buildGetParams(filters);
    params.append("page", page.toString());
    params.append("limit", limit.toString());
    if (sortConfig && sortConfig.key) {
        params.append("sortKey", sortConfig.key);
        if (sortConfig.direction) params.append("sortDirection", sortConfig.direction);
    }
    return apiInterfaceForJson(`/lead/v1/available?${params.toString()}`, "GET");
};

export const fetchAvailableLeadIds = (filters = {}) => {
    if (shouldUsePost(filters)) {
        return apiInterfaceForJson("/lead/v1/available/ids", "POST", buildFilterBody(filters));
    }

    const params = buildGetParams(filters);
    return apiInterfaceForJson(`/lead/v1/available/ids?${params.toString()}`, "GET");
};

export const bulkFetchLeads = (leadIds) => {
    return apiInterfaceForJson("/lead/v1/bulk-fetch", "POST", { leadIds });
};

export const fetchStatusBreakdown = (leadIds) => {
    return apiInterfaceForJson("/lead/v1/status-breakdown", "POST", { leadIds });
};

export const fetchDistinctValues = (field) => {
    return apiInterfaceForJson(`/lead/v1/distinct?field=${field}`, "GET");
};

export const fetchStatistics = (filters) => {
    return apiInterfaceForJson("/lead/v1/statistics", "POST", filters);
};

export const fetchStatisticsDistinct = (filters) => {
    return apiInterfaceForJson("/lead/v1/statistics/distinct", "POST", filters);
};

export const fetchCountriesBySourceAffiliate = (source = [], affiliate = []) => {
    const totalItems = (source?.length || 0) + (affiliate?.length || 0);
    if (totalItems > MAX_GET_ITEMS) {
        const body = {};
        if (source && source.length > 0) body.source = source;
        if (affiliate && affiliate.length > 0) body.affiliate = affiliate;
        return apiInterfaceForJson("/lead/v1/countries-by-source-affiliate", "POST", body);
    }

    const params = new URLSearchParams();
    if (source && source.length > 0) source.forEach(s => params.append("source", s));
    if (affiliate && affiliate.length > 0) affiliate.forEach(a => params.append("affiliate", a));
    return apiInterfaceForJson(`/lead/v1/countries-by-source-affiliate?${params.toString()}`, "GET");
};

export const fetchStatusesBySourceAffiliate = (source = [], affiliate = []) => {
    const totalItems = (source?.length || 0) + (affiliate?.length || 0);
    if (totalItems > MAX_GET_ITEMS) {
        const body = {};
        if (source && source.length > 0) body.source = source;
        if (affiliate && affiliate.length > 0) body.affiliate = affiliate;
        return apiInterfaceForJson("/lead/v1/statuses-by-source-affiliate", "POST", body);
    }

    const params = new URLSearchParams();
    if (source && source.length > 0) source.forEach(s => params.append("source", s));
    if (affiliate && affiliate.length > 0) affiliate.forEach(a => params.append("affiliate", a));
    return apiInterfaceForJson(`/lead/v1/statuses-by-source-affiliate?${params.toString()}`, "GET");
};

export const fetchSourcesByAffiliate = (affiliate = []) => {
    // Fetch sources filtered by affiliate only
    const params = new URLSearchParams();
    
    if (affiliate && affiliate.length > 0) {
        affiliate.forEach(a => params.append("affiliate", a));
    }
    
    return apiInterfaceForJson(`/lead/v1/sources-by-affiliate?${params.toString()}`, "GET");
};

export const saveCallLog = (callLog) => {
    return apiInterfaceForJson("/lead/v1/saveCallLog", "POST", callLog);
};

export const searchLeads = (query) => {
    return apiInterfaceForJson("/lead/v1/search", "POST", query);
};

export const getLeadDataById = (leadId) => {
    return apiInterfaceForJson("/lead/v1/" + leadId, "GET");
};

export const createLead = (leadDetails) => {
    return apiInterfaceForJson("/lead/v1/", "POST", leadDetails);
};

export const createLeadBatch = (batchDetails) => {
    return apiInterfaceForJson("/lead/v1/batch", "POST", batchDetails);
};

export const createLeadComment = (commentDetails) => {
    return apiInterfaceForJson("/lead/v1/comment", "POST", commentDetails);
};

export const deleteLeadComment = (commentDetails) => {
    return apiInterfaceForJson("/lead/v1/comment", "DELETE", commentDetails);
};

export const updateLead = (leadDetails) => {
    return apiInterfaceForJson("/lead/v1/", "PUT", leadDetails);
};

export const deleteLead = (leadDetails) => {
    return apiInterfaceForJson("/lead/v1/delete", "POST", leadDetails);
};

export const bulkDeleteLeads = (leadIds) => {
    return apiInterfaceForJson("/lead/v1/bulk-delete", "POST", { leadIds });
};

export const bulkUpdateLeads = (updateData) => {
    return apiInterfaceForJson("/lead/v1/bulk-update", "POST", updateData);
};

export const bulkAssignLeads = (assignData) => {
    return apiInterfaceForJson("/lead/v1/bulk-assign", "POST", assignData);
};

// Bulk-clean lead history (admin only). Wipes call/comment/assignment history
// from the selected leads and resets their createdAt to now so they appear fresh.
// Sets cleanedAt/cleanedBy/cleanedCount audit fields.
export const bulkCleanLeadHistory = (payload) => {
    return apiInterfaceForJson("/lead/v1/bulk-clean-history", "POST", payload);
};

export const shufflePreview = (previewData) => {
    return apiInterfaceForJson("/lead/v1/shuffle-preview", "POST", previewData);
};

export const shuffleLeads = (shuffleData) => {
    return apiInterfaceForJson("/lead/v1/shuffle", "POST", shuffleData);
};

export const runDuplicateCheck = (source) => {
    const body = source ? { source } : {};
    return apiInterfaceForJson("/lead/v1/duplicate-check", "POST", body);
};

export const clearDuplicateFlags = () => {
    return apiInterfaceForJson("/lead/v1/clear-duplicate-flags", "POST");
};

export const deleteDuplicateLeads = (leadIds) => {
    return apiInterfaceForJson("/lead/v1/delete-duplicates", "POST", { leadIds });
};

export const uploadScreenshots = (leadId, files) => {
    const formData = new FormData();
    formData.append("leadId", leadId);
    files.forEach((file) => {
        formData.append("screenshots", file);
    });
    
    return apiInterfaceForFormData(`/lead/v1/${leadId}/screenshots`, "POST", formData);
};

export const deleteScreenshot = (leadId, screenshotId) => {
    return apiInterfaceForJson(`/lead/v1/${leadId}/screenshots/${screenshotId}`, "DELETE");
};

export const checkEmails = (emails) => {
    return apiInterfaceForJson("/lead/v1/check-emails", "POST", { emails });
};